import { useEffect, useState } from "react";
import { inspektorSecimleri, istanbulGunu, type InspektorSecenegi, type NdtYontemi, type YeterlilikBulgusu } from "@oasis/domain";
import { Band, Buton, Cip, Etiket, Modal, etiket } from "@/components/parcalar";
import { BOS_SAYFA, SayfaAlani, sayfaAlanlari, sayfaGecerli, type SayfaSecimi } from "@/components/pdf-sayfa";
import { api } from "@/lib/api";
import { cevir } from "@/lib/dil";
import { NDT_INSPEKTOR_YOLU, UYARI_DUGME_KILIDI_MS, bulguMetni, imzalayanAlanlari, imzalayanGosterimi, onSecim, secimYeterliligi, seciliInspektorler, yuklemeAdimi, type Imzalayan, type PdfImzasi, type UyariTuru } from "@/lib/ndt-imzalayan";
import { dosyaSec } from "@/lib/veri";

/**
 * D-062: NDT raporunun imzalayanı. İki yükleme penceresi (Finishing "Upload NDT report" / "+ NDT", Component "Upload Report PDF")
 * AYNI parçaları kullanır: "Signed by (NDT inspector)" alanı ve imzalayan seçilmeden yüklemenin uyarı ve onay adımı.
 * Kural: imzalayan zorunlu değildir, ama NDT raporu ya imzalayanla ya da açık onayla yüklenir (sunucu ikisi de olmayan isteği
 * reddeder). Saf yardımcılar `lib/ndt-imzalayan.ts`'te; ortak PDF penceresi (`pdf-sayfa.tsx`) değişmedi.
 *
 * D-064: imzalayan seçiliyse yükleme düğmesi önce seçilenlerin NDT sertifikalarına bakar (yöntem, seviye, geçerlilik sonu; kural
 * alan paketinde). Yetersizse istek gitmez, aynı uyarı adımı ikinci türüyle ("yetersiz") açılır: nedenler satır satır,
 * "Upload anyway" · "Back". Uyarı ENGELLEMEZ: "Upload anyway" bugünkü imzalayanlı isteği gönderir; sunucu sertifikaya bakmaz,
 * yetersizlik ve kabul saklanmaz.
 */

/** Project documents'taki "+ Add" seçim kutusuyla aynı biçim. */
const EKLE_SECIMI = "h-kontrol-kucuk w-24 rounded-md border border-line bg-surface px-2.5 text-sm font-medium text-ink transition hover:border-line-strong disabled:cursor-not-allowed disabled:opacity-45";

type KutukDurumu = { liste: InspektorSecenegi[] | null; hata: string | null };
const OKUNUYOR: KutukDurumu = { liste: null, hata: null };

/**
 * Registers › NDT inspectors'ın seçim listesi; yalnız pencere açıkken okunur (her açılışta taze). `liste` null = okunuyor.
 * Okunamazsa liste boş kalır ve hata alanın altında görünür; rapor yine onayla imzalayansız yüklenebilir.
 */
export function useInspektorler(acik: boolean): KutukDurumu {
  const [durum, setDurum] = useState<KutukDurumu>(OKUNUYOR);
  useEffect(() => {
    if (!acik) {
      setDurum(OKUNUYOR);
      return;
    }
    let bitti = false;
    api
      .get<InspektorSecenegi[]>(NDT_INSPEKTOR_YOLU)
      .then((liste) => { if (!bitti) setDurum({ liste: Array.isArray(liste) ? liste : [], hata: null }); })
      .catch((e: unknown) => { if (!bitti) setDurum({ liste: [], hata: e instanceof Error ? e.message : cevir("Failed to load") }); });
    return () => { bitti = true; };
  }, [acik]);
  return durum;
}

/** Açık uyarı adımı: türü ve, "yetersiz" türünde, uyarı açıldığı andaki bulgular. */
type UyariDurumu = { tur: "imzalayansiz" } | { tur: "yetersiz"; bulgular: YeterlilikBulgusu[] };

/**
 * Pencerenin imzalayan durumu: kütük, seçili kimlikler ve uyarı adımı. `pdfler` verilirse (Component) kullanıcı seçime
 * dokunana kadar ön-seçim geçerlidir (`onSecim`). Pencere kapanınca seçim ve uyarı adımı sıfırlanır.
 * `uyariAc` o anki seçimi sabitler: kütük uyarı açıldıktan sonra okunursa ön-seçim araya girmez, kullanıcı gördüğünü onaylar.
 *
 * D-064: `yontemler` raporun yöntemleridir (Component: adım adından; Finishing: boş, yöntem aranmaz). `bulgular` o anki seçimin
 * sertifika yeterliliğidir (boş = yeterli ya da ölçülmedi); ölçüm günü bugündür (Europe/Istanbul). `uyari` açık mı (boolean),
 * `uyariTuru` hangi tür, `uyariBulgulari` uyarı açıldığı anda sabitlenen bulgular. `uyariAc` seçim boşsa "imzalayansiz",
 * doluysa "yetersiz" türünü açar.
 */
export function useImzalayanSecimi(acik: boolean, pdfler?: readonly PdfImzasi[], yontemler: readonly NdtYontemi[] = []) {
  const { liste, hata } = useInspektorler(acik);
  /** null: kullanıcı seçime henüz dokunmadı, ön-seçim geçerli. */
  const [elle, setElle] = useState<string[] | null>(null);
  /** null: uyarı adımı kapalı. */
  const [uyari, setUyari] = useState<UyariDurumu | null>(null);
  useEffect(() => {
    if (!acik) {
      setElle(null);
      setUyari(null);
    }
  }, [acik]);
  const kutuk = liste ?? [];
  const secili = seciliInspektorler(elle ?? onSecim(pdfler ?? [], kutuk), kutuk).map((k) => k.id);
  const bulgular = secili.length ? secimYeterliligi(secili, kutuk, yontemler, istanbulGunu()) : [];
  const uyariTuru: UyariTuru | null = uyari?.tur ?? null;
  return {
    liste,
    hata,
    secili,
    sec: (idler: string[]) => setElle(idler),
    bulgular,
    uyari: uyari !== null,
    uyariTuru,
    uyariBulgulari: uyari?.tur === "yetersiz" ? uyari.bulgular : [],
    uyariAc: () => {
      setElle(secili);
      setUyari(secili.length ? { tur: "yetersiz", bulgular } : { tur: "imzalayansiz" });
    },
    uyariKapat: () => setUyari(null),
  };
}

/**
 * "Signed by (NDT inspector)": seçili inspektörler çip + ✕; "+ Add" yerel seçim kutusu aktif ve henüz seçilmemiş inspektörleri ad
 * sırasıyla sunar (alan paketi: `inspektorSecimleri`). Birden çok inspektör seçilebilir; alan isteğe bağlıdır.
 */
export function ImzalayanAlani({ liste, secili, onChange, disabled, hata }: { liste: readonly InspektorSecenegi[] | null; secili: readonly string[]; onChange: (idler: string[]) => void; disabled?: boolean; hata?: string | null }) {
  const kutuk = liste ?? [];
  const secenekler = inspektorSecimleri(kutuk, secili);
  const baslik = cevir("Signed by (NDT inspector)");
  return (
    <div className="mt-3">
      <label className={etiket}>{baslik}</label>
      <span className="flex flex-wrap items-center gap-1">
        {seciliInspektorler(secili, kutuk).map((k) => (
          <Etiket key={k.id} tone="info">
            {k.ad}
            <button type="button" className="disabled:cursor-not-allowed disabled:opacity-45" title={cevir("Remove")} aria-label={`${cevir("Remove")}: ${k.ad}`} disabled={disabled} onClick={() => onChange(secili.filter((x) => x !== k.id))}>✕</button>
          </Etiket>
        ))}
        <select
          className={EKLE_SECIMI}
          aria-label={`${cevir("+ Add")}: ${baslik}`}
          value=""
          disabled={disabled || !secenekler.length}
          onChange={(e) => {
            if (e.target.value) onChange([...secili, e.target.value]);
          }}
        >
          <option value="">{cevir("+ Add")}</option>
          {secenekler.map((k) => <option key={k.id} value={k.id}>{k.ad}</option>)}
        </select>
      </span>
      {hata && <p className="mt-1 text-xs text-bad">{hata}</p>}
      <p className="mt-1 text-xs text-ink-3">{cevir("Optional. Not in the list? The PM or the system administrator adds inspectors in Registers › NDT inspectors.")}</p>
    </div>
  );
}

/**
 * Uyarı ve onay adımı (iki pencerede tek desen): imzalayan seçilmeden yükleme düğmesine basılınca istek gitmez, düğme satırının
 * yerine bu sarı uyarı ve iki düğme gelir. "Back" uyarıyı kapatır; onay isteği `imzalayanOnay` alanıyla gönderir ve olay
 * kaydına kim ve zamanla düşer. Tarayıcının onay kutusu kullanılmaz. Düğmeler dar ekranda alt alta, geniş ekranda yan yana.
 * Uyarı her açılışta yeniden kurulur ve iki düğme kısa süre pasif başlar (`UYARI_DUGME_KILIDI_MS`): yükleme düğmesine çift
 * dokunuşun ikincisi uyarıyı kapatmasın ya da onaylamasın. Sıra sabittir: onay üstte, "Back" altta.
 *
 * D-064: parça iki tür çizer; kabuk (sarı bant, düğme kilidi, sıra, çift tıklama koruması, "Back") ortaktır. `tur` verilmezse
 * ya da "imzalayansiz" ise yukarıdaki imzalayansız uyarı aynen çizilir. "yetersiz" türünde (seçilen imzalayanın NDT sertifikası
 * yetmiyor): kalın başlık · yetersiz her imzalayan için tek neden satırı (`bulgular`, imzalayan sırasıyla) · neye bakıldığı
 * (`yontemler`: tek yöntem, birden çok yöntem ya da boş = raporun yöntemi bilinmiyor) · raporun yine yüklenebileceği; kabul
 * düğmesi "Upload anyway". Bu türde kabul bir kayıt bırakmaz: istek bugünkü imzalayanlı istektir, onay alanı gitmez.
 */
export function ImzalayanUyarisi({ onOnayla, onGeri, tur = "imzalayansiz", bulgular = [], yontemler = [] }: { onOnayla: () => void | Promise<void>; onGeri: () => void; tur?: UyariTuru; bulgular?: readonly YeterlilikBulgusu[]; yontemler?: readonly NdtYontemi[] }) {
  /** Açılış kilidi: düğmeler ilk çizimde pasiftir, süre dolunca açılır. */
  const [hazir, setHazir] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setHazir(true), UYARI_DUGME_KILIDI_MS);
    return () => clearTimeout(t);
  }, []);
  /** Çift tıklama ikinci yükleme göndermesin. */
  const [mesgul, setMesgul] = useState(false);
  const onayla = async () => {
    setMesgul(true);
    try {
      await onOnayla();
    } finally {
      setMesgul(false);
    }
  };
  const yetersiz = tur === "yetersiz";
  /** Neye bakıldığı: raporun yöntemi tekse o yöntem, birden çoksa "birinde", bilinmiyorsa (Finishing, adında yöntem yazmayan adım) herhangi biri. */
  const dayanak =
    yontemler.length === 1
      ? cevir("Checked for each signer: a Level 2 or 3 {0} certificate that has not expired.", yontemler[0])
      : yontemler.length > 1
        ? cevir("Checked for each signer: a Level 2 or 3 certificate that has not expired, in one of {0}.", yontemler.join(" / "))
        : cevir("Checked for each signer: a Level 2 or 3 certificate that has not expired, in any method. OASIS does not know the method of this report.");
  return (
    <>
      <Band tip="not" className="mt-3">
        {yetersiz ? (
          <>
            <p className="font-semibold">{cevir("The NDT certificate of the selected signer is not sufficient for this report.")}</p>
            {bulgular.map((b) => <p key={b.inspektorId} className="mt-1 font-medium">{bulguMetni(b)}</p>)}
            <p className="mt-1">{dayanak}</p>
            <p className="mt-1">{cevir("The report can still be uploaded. The PM or the system administrator adds or corrects certificates in Registers › NDT inspectors.")}</p>
          </>
        ) : (
          <>
            <p className="font-semibold">{cevir("No NDT inspector is selected as the signer of this report.")}</p>
            <p className="mt-1">{cevir("The report can be uploaded now. The signer must be entered later on Project documents › Riser › Reports — test equipment and signer. If the inspector is not in the list, the PM or the system administrator first adds the inspector and the certificate in Registers › NDT inspectors.")}</p>
            <p className="mt-1">{cevir("Your confirmation is written to the audit log with your name and the time.")}</p>
          </>
        )}
      </Band>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <Buton tur="birincil" className="justify-center" disabled={!hazir} mesgul={mesgul} onClick={onayla}>{yetersiz ? cevir("Upload anyway") : cevir("Confirm and upload without signer")}</Buton>
        <Buton className="justify-center" disabled={!hazir || mesgul} onClick={onGeri}>{cevir("Back")}</Buton>
      </div>
    </>
  );
}

/**
 * Yüklü NDT raporunun yanında imzalayan: adlar ("Signed by: …") ya da sarı "signer missing". Bağlantı değildir, burada
 * düzenleme yok: imzalayan sonradan Project documents › Riser tablosundan girilir. null ise (imzalayan sorulmayan yükleme) boş.
 */
export function ImzalayanEtiketi({ imzalayanlar }: { imzalayanlar: readonly Imzalayan[] | null | undefined }) {
  const g = imzalayanGosterimi(imzalayanlar);
  if (g.tur === "yok") return null;
  return g.tur === "var" ? <span className="text-xs text-ink-3">{cevir("Signed by: {0}", g.adlar)}</span> : <Etiket tone="warn">{cevir("signer missing")}</Etiket>;
}

/** Pencerenin sonucu: seçilen PDF ve `dosyaYukle`'ye verilecek form alanları (sayfa aralığı + imzalayan ya da onay). */
export type NdtYukleme = { dosya: File; alanlar: Record<string, string> };

/**
 * Finishing'in NDT raporu yükleme penceresi (ortak PDF penceresinden ayrıldı; HT, Coating ve Assembly orada kalır): Select PDF ·
 * Partial + sayfa · "Signed by (NDT inspector)" · Upload · Cancel. Pencere sonuçla kapanır (`bitir`), yüklemeyi çağıran yapar;
 * vazgeçilirse null. İmzalayan seçilmeden "Upload"a basılırsa uyarı adımına geçer: üstteki alanlar kilitlenir, seçilen PDF ve
 * sayfa korunur.
 *
 * D-064: imzalayan seçiliyse ve sertifikası yetersizse aynı adım "yetersiz" türüyle açılır. Hat NDT raporu adım değildir:
 * raporun yöntemi bilinmez (kancaya yöntem verilmez), herhangi bir yöntemde Seviye 2 ya da 3 ve süresi geçmemiş sertifika yeter.
 * "Upload anyway" `gonder(true)` çağırır; imzalayan seçiliyken `imzalayanAlanlari` onayı yok sayar, istek imzalayanlarla gider.
 */
export function NdtYuklemePenceresi({ baslik, bitir }: { baslik: string; bitir: (s: NdtYukleme | null) => void }) {
  const [dosya, setDosya] = useState<File | null>(null);
  const [sayfa, setSayfa] = useState<SayfaSecimi>(BOS_SAYFA);
  const imza = useImzalayanSecimi(true);
  const gonder = (onay: boolean) => {
    if (dosya) bitir({ dosya, alanlar: { ...sayfaAlanlari(sayfa), ...imzalayanAlanlari(imza.secili, onay) } });
  };
  return (
    <Modal baslik={baslik} onKapat={() => bitir(null)}>
      <div className="flex flex-wrap items-center gap-2">
        <Buton kucuk disabled={imza.uyari} onClick={async () => { const f = await dosyaSec(".pdf,application/pdf"); if (f) setDosya(f); }}>{dosya ? cevir("Change file") : cevir("Select PDF")}</Buton>
        {dosya ? <Cip>📄 {dosya.name}</Cip> : <span className="text-sm text-ink-3">{cevir("not selected")}</span>}
      </div>
      <SayfaAlani deger={sayfa} onChange={setSayfa} disabled={imza.uyari} />
      <ImzalayanAlani liste={imza.liste} hata={imza.hata} secili={imza.secili} onChange={imza.sec} disabled={imza.uyari} />
      {imza.uyari ? (
        <ImzalayanUyarisi tur={imza.uyariTuru ?? "imzalayansiz"} bulgular={imza.uyariBulgulari} yontemler={[]} onOnayla={() => gonder(true)} onGeri={imza.uyariKapat} />
      ) : (
        <div className="mt-4 flex gap-2">
          <Buton tur="birincil" disabled={!dosya || !sayfaGecerli(sayfa)} onClick={() => { if (yuklemeAdimi(true, imza.secili, imza.bulgular) === "gonder") gonder(false); else imza.uyariAc(); }}>{cevir("Upload")}</Buton>
          <Buton onClick={() => bitir(null)}>{cevir("Cancel")}</Buton>
        </div>
      )}
    </Modal>
  );
}
